import { getAvailableActionsForSubject, updatePermissionGrants } from './permissions.util'
import { CurecallMultiSelect } from '@honestica/curecall-ui'
import {
  actionToIntlTitle,
  EPermissionGrantActions,
  EPermissionGrantSubjects,
  TPermissionGrant,
} from './permissions.type'
import { useTranslation } from 'react-i18next'

type GrantActionOption = {
  label: string
  value: EPermissionGrantActions
}

/**
 * Manipulates actions linked to given subject in the `allGrants` object
 * Will not change actions from other subjects
 * @param {EPermissionGrantSubjects} subject
 * @param {TPermissionGrant[]} allGrants
 * @param {(allGrants: TPermissionGrant[]) => void} onChange
 * @returns {JSX.Element}
 * @constructor
 */
export function PermissionGrantActionsMultiSelect({
  subject,
  allGrants,
  onChange,
}: {
  subject: EPermissionGrantSubjects
  allGrants: TPermissionGrant[]
  onChange: (allGrants: TPermissionGrant[]) => void
}): React.JSX.Element {
  const { t } = useTranslation()

  // Raw Actions
  const availableActionsForSubject = getAvailableActionsForSubject(subject)

  const selectedActionsForSubject = allGrants?.find((g) => g.subject === subject)?.actions ?? []
  const existingGrant = allGrants?.find((g) => g.subject === subject)
  if (existingGrant) {
    existingGrant.actions = selectedActionsForSubject
  }

  // Raw Actions => MultiSelect options
  const selectableActionsOptions = availableActionsForSubject.map((action) => ({
    label: t(actionToIntlTitle[action]),
    value: action,
  }))
  const selectedActionOptions = selectableActionsOptions.filter((action) =>
    selectedActionsForSubject.includes(action.value),
  )

  // String displayed when MultiSelect can't display all selected options as badges
  const isActionAllSelected: boolean = selectedActionOptions.some(
    (action) => action.value === EPermissionGrantActions.ALL,
  )

  const handleOnSelect = (selectedActionsOptions: GrantActionOption[]) => {
    const existingGrantIndex = allGrants.findIndex((g) => g.subject === subject)

    const selectedActions = selectedActionsOptions.map((actionOption) => actionOption.value)
    const existingGrantActions = allGrants[existingGrantIndex]?.actions ?? []
    const diffActions: EPermissionGrantActions[] =
      selectedActions.length > existingGrantActions.length ? selectedActions : existingGrantActions

    for (let i = 0; i < diffActions.length; i++) {
      if (diffActions[i] && !selectedActions.includes(diffActions[i] as EPermissionGrantActions)) {
        const action = diffActions[i]
        allGrants = updatePermissionGrants({ allGrants, operation: 'REMOVE', subject, action })
        break
      } else if (
        diffActions[i] &&
        !existingGrantActions.includes(diffActions[i] as EPermissionGrantActions)
      ) {
        const action = diffActions[i]
        allGrants = updatePermissionGrants({ allGrants, operation: 'ADD', subject, action })
        break
      }
    }

    onChange(allGrants)
  }

  return (
    <CurecallMultiSelect
      items={selectableActionsOptions}
      selectedItems={selectedActionOptions}
      selectedElementsLabel={isActionAllSelected ? t(actionToIntlTitle[EPermissionGrantActions.ALL]) : ''}
      onSelect={handleOnSelect}
      wrapperClassName="c-w-[465px] c-relative"
      selectedElementsTriggerCount={isActionAllSelected ? 0 : 100}
    />
  )
}
